/**
 * Cluster GEO-DATA Fase C — ComunePicker
 *
 * Picker async per comuni italiani (master_data_comuni). Memorizza
 * 2 valori paralleli: il NOME del comune (compatibile con il modello
 * dati esistente che usa stringhe libere "Roma"/"Milano") e, se
 * disponibile, il `code_belfiore` come metadato.
 *
 * Differenza vs NazionePicker: 7.894 record totali → ricerca SERVER-SIDE
 * con debounce 220ms. Non c'è cache process-wide; ogni apertura/typing
 * triggera una nuova query con `q=`.
 *
 * Props:
 *   value             (string) nome del comune corrente
 *   onChange          (fn)     callback (nome, comuneObj | null)
 *                              comuneObj = {code_belfiore, nome, provincia_sigla, cap: [...]}
 *   provinciaFilter   (string) sigla provincia (es. "MI") per pre-filtrare la lista
 *   disabled          (bool)
 *   testId            (string)
 *   placeholder       (string)
 *   compact           (bool)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { api } from "@/lib/api";

export default function ComunePicker({
  value, onChange, provinciaFilter, disabled, testId,
  placeholder = "Comune…", compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const reqRef = useRef(0);

  // Carica lista quando popover aperto, oppure quando cambia query/provincia
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const my = ++reqRef.current;
      const params = new URLSearchParams();
      params.set("page_size", "50");
      if (query.trim()) params.set("q", query.trim());
      if (provinciaFilter) params.set("provincia", provinciaFilter);
      try {
        const r = await api.get(`/master-data/comuni?${params.toString()}`);
        if (my === reqRef.current) {
          setItems((r.data && r.data.items) || []);
        }
      } catch {
        if (my === reqRef.current) setItems([]);
      } finally {
        if (my === reqRef.current) setLoading(false);
      }
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, query, provinciaFilter]);

  const heightCls = compact ? "h-8 text-xs px-2" : "h-10 text-sm px-3";

  const triggerLabel = value
    ? <span>{value}</span>
    : <span className="text-slate-400">{placeholder}</span>;

  const norm = (value || "").toString().toLowerCase();
  const selectedKey = useMemo(
    () => items.find((c) => (c.nome || "").toLowerCase() === norm)?.code_belfiore || null,
    [items, norm],
  );

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            data-testid={testId}
            className={cn(
              "w-full justify-between font-normal bg-white",
              heightCls,
              value && !disabled ? "pr-12" : "",
            )}
          >
            <span className="truncate text-left flex-1">{triggerLabel}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 ml-1 shrink-0" />
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange?.("", null);
            }}
            data-testid={testId ? `${testId}-clear` : undefined}
            title="Rimuovi selezione"
            className="absolute right-7 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-rose-600 rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={provinciaFilter ? `Cerca comune in ${provinciaFilter}…` : "Cerca comune…"}
            value={query}
            onValueChange={setQuery}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            {loading && (
              <div className="p-3 text-xs text-slate-400">Caricamento…</div>
            )}
            {!loading && items.length === 0 && (
              <CommandEmpty>
                {query.trim()
                  ? `Nessun comune trovato per "${query}".`
                  : "Inizia a digitare per cercare un comune…"}
              </CommandEmpty>
            )}
            {!loading && items.length > 0 && (
              <CommandGroup>
                {items.map((c) => (
                  <CommandItem
                    key={c.code_belfiore}
                    value={c.code_belfiore}
                    onSelect={() => {
                      onChange?.(c.nome, c);
                      setOpen(false);
                    }}
                    data-testid={testId ? `${testId}-opt-${c.code_belfiore}` : undefined}
                  >
                    <Check className={cn("mr-2 h-4 w-4",
                      selectedKey === c.code_belfiore ? "opacity-100" : "opacity-0")} />
                    <div className="flex items-baseline gap-2 flex-1 min-w-0">
                      <span className="text-sm truncate">{c.nome}</span>
                      <span className="font-mono text-xs text-slate-500">{c.provincia_sigla}</span>
                      <span className="font-mono text-[10px] text-slate-400">{c.code_belfiore}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
